// components/configuration/SettingsField.jsx
import React from 'react';
import { FIELD_TYPES } from '../../../constants/settingsConfig';

/**
 * Generic settings field component
 * Renders different input types based on field configuration
 */
const SettingsField = ({ field, value, onChange, configs }) => {
  // Check if this field should be shown (based on dependsOn)
  if (field.dependsOn && !configs[field.dependsOn]) {
    return null; // Hide field if dependency is not met
  }

  const renderInput = () => {
    switch (field.type) {
      case FIELD_TYPES.NUMBER:
        return (
          <input
            type="number"
            value={value ?? field.defaultValue}
            onChange={(e) => onChange(field.key, Number(e.target.value))}
            min={field.min}
            max={field.max}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );

      case FIELD_TYPES.TEXT:
      case FIELD_TYPES.EMAIL:
        return (
          <input
            type={field.type === FIELD_TYPES.EMAIL ? 'email' : 'text'}
            value={value ?? field.defaultValue}
            onChange={(e) => onChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );

      case FIELD_TYPES.SELECT:
        return (
          <select
            value={value ?? field.defaultValue}
            onChange={(e) => onChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {field.options.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        );

      case FIELD_TYPES.TOGGLE:
        return (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <div className="font-medium text-gray-900">{field.label}</div>
              {field.helpText && (
                <div className="text-sm text-gray-500">{field.helpText}</div>
              )}
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={value ?? field.defaultValue}
                onChange={(e) => onChange(field.key, e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        );

      default:
        return null;
    }
  };

  // Toggle fields have their own layout
  if (field.type === FIELD_TYPES.TOGGLE) {
    return renderInput();
  }

  // Standard field layout for other types
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {field.label}
      </label>
      {renderInput()}
      {field.helpText && (
        <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
      )}
    </div>
  );
};

export default SettingsField;